import { useCallback, useState } from "react";

import {
    createModifierSet,
    deleteModifierSet as deleteModifierSetRequest,
    updateModifierSet,
} from "services/modifierSets/rest";
import { ModifierSetDto } from "services/modifierSets/restModels";
import { ModifierSetRequestDto } from "services/modifierSets/restRequests";
import { UseNotificationSnackbarReturn } from "shared/ui";

import { sortModifierSetsByName } from "features/ModifierSets/lib/modifierSetOrder";

export interface UseModifierSetMutationsResult {
    saving: boolean;
    // Возвращают сохранённый набор / оставшийся каталог либо null при ошибке — что делать
    // дальше (закрыть диалог, привязать к позиции), решает вызывающая фича.
    saveModifierSet: (
        values: ModifierSetRequestDto,
        modifierSetId: number | null
    ) => Promise<ModifierSetDto | null>;
    removeModifierSet: (modifierSetId: number) => Promise<ModifierSetDto[] | null>;
}

/*
* Мутации набора — одни на все фичи: набор правят и со своей страницы, и из редактора
* позиции. Хук сам обновляет каталог (в порядке, который отдаёт бэк) и показывает тосты,
* чтобы тексты и поведение не разъезжались между двумя точками входа.
* */
export const useModifierSetMutations = (
    modifierSets: ModifierSetDto[],
    setModifierSets: (next: ModifierSetDto[]) => void,
    notify: UseNotificationSnackbarReturn
): UseModifierSetMutationsResult => {
    const [saving, setSaving] = useState<boolean>(false);

    const saveModifierSet = useCallback(
        async (
            values: ModifierSetRequestDto,
            modifierSetId: number | null
        ): Promise<ModifierSetDto | null> => {
            setSaving(true);
            try {
                if (modifierSetId) {
                    const updated = await updateModifierSet(modifierSetId, values);

                    if (updated) {
                        setModifierSets(
                            sortModifierSetsByName(
                                modifierSets.map((item) =>
                                    item.id === updated.id ? updated : item
                                )
                            )
                        );
                    }

                    notify.showSuccess("Набор сохранён");
                    return updated;
                }

                const created = await createModifierSet(values);

                if (created) {
                    setModifierSets(sortModifierSetsByName([...modifierSets, created]));
                }

                notify.showSuccess("Набор добавлен");
                return created;
            } catch (e) {
                notify.showError("Не удалось сохранить набор", e);
                return null;
            } finally {
                setSaving(false);
            }
        },
        [modifierSets, notify, setModifierSets]
    );

    const removeModifierSet = useCallback(
        async (modifierSetId: number): Promise<ModifierSetDto[] | null> => {
            setSaving(true);
            try {
                const remaining = await deleteModifierSetRequest(modifierSetId);

                if (remaining) setModifierSets(remaining);

                notify.showSuccess("Набор удалён");
                return remaining;
            } catch (e) {
                notify.showError("Не удалось удалить набор", e);
                return null;
            } finally {
                setSaving(false);
            }
        },
        [notify, setModifierSets]
    );

    return { saving, saveModifierSet, removeModifierSet };
};
